import React, { ChangeEvent } from "react"
import { Box, TextField, MenuItem } from "@mui/material"
import Icon from "@mdi/react"
import { mdiChevronUp, mdiChevronDown, mdiPencil, mdiInformation } from "@mdi/js"

import Price from "components/Price"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"
import EditLockingSystemComponentConfigurations from "../EditComponent/EditLockingSystemComponentConfigurations"
import TextInput from "components/Forms/TextInput"

import useToggle from "@secureo/common/hooks/useToggle"

import { netToGross } from "@secureo/common/utils/tax"

import {
	LockingSystemComponentType,
	LockingSystemPreviewProduct,
	LockingSystemComponent as LockingSystemComponentInterface,
	LockingSystemCharges,
} from "@secureo/common/typings/LockingSystem"

const getFallbackName = (type: LockingSystemComponentType, index: number) => {
	switch (type) {
		case "lock":
			return `Tür ${index + 1}`
		case "key":
			return `Schlüssel ${index + 1}`
	}

	return ""
}

const getTypeForConfiguration = (type: LockingSystemComponentType) => {
	switch (type) {
		case "lock":
			return `Zylinderkonfiguration`
		case "key":
			return `Schlüsselkonfiguration`
	}
	return ""
}

const LockingSystemComponent = ({
	isLoading,
	isFrozen,
	index,
	lockingSystemComponent,
	setComponentQuantity,
	setEditComponentModalUuid,
	taxRate,
	availableLockingComponents,
	availableKeyComponents,
	charges,
	setSystemComponentName,
	setComponentProduct,
	setComponentSystem,
	setComponentOuterLength,
	setComponentInnerLength,
}: Props) => {
	const [showConfigurations, toggleShowConfigurations] = useToggle()

	const {
		uuid,
		type,
		name,
		configurationsByConfigurationGroup,
		quantity,
		pricePortions,
		attributes,
		selectedSystem,
		selectedOuterLength,
		selectedInnerLength,
		masterVariantSku,
	} = lockingSystemComponent

	const fallbackName = getFallbackName(type, index)
	const typeForConfigurationHeadline = getTypeForConfiguration(type)

	const onComponentQuantityChange = (quantity: number) => {
		setComponentQuantity(uuid, quantity)
	}

	const onComponentNameChange = (event: any) => {
		const name = event.target.value

		setSystemComponentName(uuid, name)
	}

	const onLockingSystemProductChange = (
		event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	) => {
		const sku = event.target.value
		const lockingSystemPreviewProduct = availableLockingComponents.find(
			(previewProduct) => previewProduct.sku === sku,
		)

		setComponentProduct(uuid, lockingSystemPreviewProduct)
	}

	const isLockingComponent = type === "lock"

	const configurations = Object.values(configurationsByConfigurationGroup)
		.map((configurationGroup) => Object.values(configurationGroup))
		.flat()

	const availableComponents = isLockingComponent
		? availableLockingComponents
		: availableKeyComponents

	return (
		<>
			<tr>
				<td
					style={{
						width: 40,
						paddingTop: 5,
						textAlign: "center",
					}}
				>
					<Box mr={1} style={{ display: "flex", alignItems: "center" }}>
						<button
							style={{
								background: "rgba(70, 145, 206, 0.25)",
								width: 40,
								height: 40,
								borderRadius: 3,
								textAlign: "center",
								border: "none",
								outline: "none",
								cursor: "pointer",
								marginLeft: 4,
								marginRight: 10,
							}}
							disabled={!setEditComponentModalUuid || isFrozen}
							onClick={() => setEditComponentModalUuid(uuid)}
						>
							<Icon
								path={isFrozen ? mdiInformation : mdiPencil}
								size={1}
								color="#4691CE"
							/>
						</button>
						<strong>{index + 1}</strong>
					</Box>
				</td>
				<td style={{ width: 200 }}>
					<TextInput
						name="name"
						label={type === "lock" ? "Türname" : "Schlüsselname"}
						value={name}
						onChange={onComponentNameChange}
						placeholder={fallbackName}
						disabled={isLoading || !setSystemComponentName || isFrozen}
						style={{ marginTop: 5 }}
						fullWidth
					/>
				</td>
				<td style={{ width: 200 }}>
					<TextField
						label={isLockingComponent ? "Zylinder" : "Schlüssel"}
						value={masterVariantSku}
						onChange={onLockingSystemProductChange}
						margin="normal"
						variant="filled"
						select
						fullWidth
						disabled={
							isLoading ||
							!onLockingSystemProductChange ||
							availableComponents.length < 2 ||
							isFrozen
						}
						style={{ marginTop: 8 }}
					>
						{availableComponents.map((lockingComponent) => {
							const { sku, name } = lockingComponent

							return (
								<MenuItem key={sku} value={sku}>
									{name}
								</MenuItem>
							)
						})}
					</TextField>
				</td>
				<td>
					<EditLockingSystemComponentConfigurations
						isLoading={isLoading}
						disabled={isFrozen}
						attributes={attributes}
						selectedSystem={selectedSystem}
						selectedOuterLength={selectedOuterLength}
						selectedInnerLength={selectedInnerLength}
						setSelectedSystem={(selectedSystem: string) =>
							setComponentSystem(uuid, selectedSystem)
						}
						setSelectedOuterLength={(selectedOuterLength: number) =>
							setComponentOuterLength(uuid, selectedOuterLength)
						}
						setSelectedInnerLength={(selectedInnerLength: number) =>
							setComponentInnerLength(uuid, selectedInnerLength)
						}
					/>
				</td>
				<td style={{ width: 300 }} />
				{!isLockingComponent && (
					<td
						style={{
							width: 120,
						}}
					>
						<div style={{ display: "flex", justifyContent: "center" }}>
							<ChangeAmountButton
								quantity={quantity}
								minQuantity={1}
								disabled={isLoading || isFrozen}
								onChange={onComponentQuantityChange}
							/>
						</div>
					</td>
				)}

				<td style={{ textAlign: "right", width: 100 }}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							background: "#263739",
							color: "white",
							padding: 7,
							borderRadius: 4,
						}}
					>
						<div style={{ marginRight: 5 }}>
							<strong>Preis:</strong>
						</div>
						<strong>
							<Price
								priceInEuroCents={
									pricePortions.productSingleGrossPriceCents * quantity +
									(charges?.byUuid?.[uuid].totalGrossChargeCents ?? 0)
								}
							/>
						</strong>
					</div>
				</td>
				<td style={{ textAlign: "center", width: 60 }}>
					<button
						style={{
							width: "30",
							background: "#EDEFF0",
							border: "none",
							borderRadius: 3,
							outline: "none",
							cursor: "pointer",
						}}
						onClick={toggleShowConfigurations}
						disabled={configurations.length === 0}
					>
						<Icon
							path={!showConfigurations ? mdiChevronDown : mdiChevronUp}
							size={1}
							style={{ padding: 0 }}
						/>
					</button>
				</td>
			</tr>

			{showConfigurations && (
				<tr>
					<td colSpan={7}>
						<Box my={3} style={{ maxWidth: 1215 }}>
							<strong style={{ textTransform: "uppercase", marginLeft: 15 }}>
								{typeForConfigurationHeadline}
							</strong>
							<Box px={2} mt={2}>
								{configurations.map((configuration) => {
									const { productId, name, singleNetPriceCents, quantity } =
										configuration

									const singleGrossPriceCents = Math.round(
										netToGross(singleNetPriceCents, taxRate),
									)

									return (
										<div
											key={productId}
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												width: "100%",
											}}
										>
											<div style={{ textAlign: "left" }}>
												{quantity > 1 ? quantity : ""} {name}
											</div>
											<div style={{ float: "right" }}>
												<Price priceInEuroCents={singleGrossPriceCents} />
											</div>
										</div>
									)
								})}
							</Box>
						</Box>
					</td>
				</tr>
			)}
		</>
	)
}

interface Props {
	isLoading: boolean
	isFrozen: boolean
	index: number
	type: LockingSystemComponentType
	lockingSystemComponent: LockingSystemComponentInterface
	taxRate: number
	setComponentQuantity?: (uuid: string, quantity: number) => void
	setEditComponentModalUuid?: (uuid: string) => void
	availableLockingComponents: LockingSystemPreviewProduct[]
	availableKeyComponents: LockingSystemPreviewProduct[]
	charges: LockingSystemCharges
	setSystemComponentName: (uuid: string, name: string) => void
	setComponentProduct: (
		uuid: string,
		lockingSystemPreviewProduct: LockingSystemPreviewProduct,
	) => void
	setComponentSystem: (uuid: string, selectedSystem: string) => void
	setComponentOuterLength: (uuid: string, selectedOuterLength: number) => void
	setComponentInnerLength: (uuid: string, selectedInnerLength: number) => void
}

export default LockingSystemComponent
